import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./DropdownField.module.css";

const MENU_MAX_HEIGHT = 220;
const MENU_MIN_HEIGHT = 140;

export default function DropdownField({
  items = [],
  value = null,
  labelKey = "name",
  placeholder = "Seleccionar",
  searchable = false,
  onSearch,
  onLoadMore,
  loading = false,
  onChange,
  renderValue,
  footerAction = null,
  className = "",
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [search, setSearch] = useState("");
  const [menuPosition, setMenuPosition] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const searchTimerRef = useRef(null);

  // El menú se renderiza en un portal con position fixed: si quedara dentro
  // del componente, agrandaría el área de scroll del form que lo contiene.
  // Se abre siempre hacia abajo; si falta lugar hasta el borde del viewport,
  // recorta su altura y el scroll interno hace el resto.
  const updateMenuPosition = useCallback(() => {
    const anchorRect = rootRef.current?.getBoundingClientRect();
    if (!anchorRect) return;

    const margin = 8;
    const gap = 2;
    const spaceBelow = window.innerHeight - anchorRect.bottom - gap - margin;
    const maxHeight = Math.min(
      MENU_MAX_HEIGHT,
      Math.max(spaceBelow, MENU_MIN_HEIGHT),
    );

    setMenuPosition({
      left: anchorRect.left,
      width: anchorRect.width,
      top: Math.min(
        anchorRect.bottom + gap,
        window.innerHeight - margin - maxHeight,
      ),
      maxHeight,
    });
  }, []);

  useEffect(() => {
    if (!isEditing) {
      setSearch("");
      setMenuPosition(null);
      clearTimeout(searchTimerRef.current);
      return;
    }

    updateMenuPosition();

    function onMouseDown(event) {
      if (
        !rootRef.current?.contains(event.target) &&
        !menuRef.current?.contains(event.target)
      ) {
        setIsEditing(false);
      }
    }

    // Si scrollea el form (o cualquier contenedor externo) el menú quedaría
    // flotando desanclado: lo cerramos. El scroll interno del menú se ignora.
    function onScroll(event) {
      if (menuRef.current?.contains(event.target)) return;
      setIsEditing(false);
    }

    function onResize() {
      setIsEditing(false);
    }

    document.addEventListener("mousedown", onMouseDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [isEditing, updateMenuPosition]);

  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  const hasValue = Boolean(value);

  const normalizedItems = useMemo(
    () => (items || []).filter((item) => item && item[labelKey]),
    [items, labelKey],
  );

  const filteredItems = useMemo(() => {
    if (onSearch) return normalizedItems;
    if (!searchable || !search.trim()) return normalizedItems;
    const q = search.trim().toLowerCase();
    return normalizedItems.filter((item) =>
      item[labelKey]?.toLowerCase().includes(q),
    );
  }, [normalizedItems, searchable, onSearch, search, labelKey]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    if (onSearch) {
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => onSearch(val), 300);
    }
  };

  const handleMenuScroll = () => {
    const el = menuRef.current;
    if (!el || !onLoadMore || loading) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      onLoadMore();
    }
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange?.(null);
    setIsEditing(false);
  };

  const handleSelect = (item) => {
    onChange?.(item);
    setIsEditing(false);
  };

  const menu =
    isEditing && menuPosition
      ? createPortal(
          <div
            className={styles.menu}
            ref={menuRef}
            style={menuPosition}
            onScroll={handleMenuScroll}
          >
            {searchable ? (
              <div className={styles.searchWrapper}>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Buscar..."
                  value={search}
                  onChange={handleSearchChange}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
              </div>
            ) : null}

            {filteredItems.length ? (
              filteredItems.map((item) => {
                const isSelected = value?.id && item?.id === value.id;

                return (
                  <button
                    type="button"
                    key={item.id || item[labelKey]}
                    className={`${styles.option} ${
                      isSelected ? styles.optionSelected : ""
                    }`}
                    onClick={() => handleSelect(item)}
                  >
                    {renderValue ? (
                      renderValue(item)
                    ) : (
                      <span className={styles.optionText}>{item[labelKey]}</span>
                    )}
                  </button>
                );
              })
            ) : !loading ? (
              <div className={styles.empty}>Sin opciones</div>
            ) : null}

            {loading ? (
              <div className={styles.loadingMore}>Cargando...</div>
            ) : null}

            {footerAction ? (
              <div className={styles.footer}>
                <button
                  type="button"
                  className={styles.footerButton}
                  onClick={() => {
                    setIsEditing(false);
                    footerAction.onClick?.();
                  }}
                >
                  {footerAction.label}
                </button>
              </div>
            ) : null}
          </div>,
          document.body,
        )
      : null;

  return (
    <div className={`${styles.root} ${className}`} ref={rootRef}>
      <button
        type="button"
        className={`${styles.display} ${isEditing ? styles.displayEditing : ""}`}
        onClick={() => setIsEditing((prev) => !prev)}
      >
        <span className={styles.displayContent}>
          {hasValue ? (
            <>
              {renderValue ? (
                renderValue(value)
              ) : (
                <span className={styles.value}>{value[labelKey]}</span>
              )}

              {isEditing ? (
                <span
                  className={styles.clearButton}
                  role="button"
                  tabIndex={0}
                  onClick={handleClear}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      handleClear(event);
                    }
                  }}
                >
                  ×
                </span>
              ) : null}
            </>
          ) : (
            <span className={styles.placeholder}>{placeholder}</span>
          )}
        </span>
      </button>

      {menu}
    </div>
  );
}
